import React, { useState, useEffect, useContext } from "react";
import { IconButton, Tooltip, Typography, Box } from '@mui/material';
import { ArrowClockwise, Check, Asterisk } from '@phosphor-icons/react';
import { ThemeContext } from 'styled-components';

/**
 * Generic wrapper component for any input with action buttons
 * Supports refresh, success, and required field indicator actions with consistent spacing
 */
const WithActions = ({
                              // Action button props
                              showRefreshButton = false,
                              onRefresh,
                              isRefreshing = false,
                              refreshTooltip = "Refresh",
                              refreshIcon = ArrowClockwise,
                              fullWidth = true,

                              showSuccessButton = false,
                              successTooltip = "Success",
                              successIcon = Check,
                              successColor,

                              // Required field indicator props
                              showRequiredIcon = false,
                              requiredIcon = Asterisk,
                              requiredTooltip = "Required field",
                              requiredIconColor,
                              requiredIconSize = 24,

                              // Right-side action button props
                              showActionButton = false,
                              onActionClick,
                              actionIcon,
                              actionTooltip = "",
                              actionButtonDisabled = false,
                              actionIconSize = 24,
                              actionIconColor,
                              actionIconType = 'phosphor', // 'phosphor' or 'mui'
                              actionIconSx, // For MUI icons, pass sx prop directly

                              // Cooldown props
                              cooldownDuration = 60, // seconds
                              cooldownWarningDuration = 5, // seconds

                              // Layout props
                              size = '40px', // Default size for both input height and button width
                              buttonWidth, // Optional override for button width (defaults to size)
                              gap = '0px',
                              inputHeight, // Optional override for input height (defaults to size)
                              inputType = 'code', // 'otp' or 'textfield'
                              forceSpacing = false, // Force spacing even when no buttons are shown
                              isButton = false, // Set to true when wrapping a button component

                              // Children (the actual input component)
                              children
                          }) => {
    const theme = useContext(ThemeContext);
    const [cooldownEndTime, setCooldownEndTime] = useState(null);
    const [showCooldownWarning, setShowCooldownWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);

    // Initialize cooldown when component mounts
    useEffect(() => {
        if (showRefreshButton) {
            const initialCooldown = Date.now() + (cooldownDuration * 1000);
            setCooldownEndTime(initialCooldown);
            setTimeRemaining(cooldownDuration);
        }
    }, [showRefreshButton, cooldownDuration]);

    // Timer to update remaining time
    useEffect(() => {
        if (!cooldownEndTime || !showRefreshButton) return;

        const timer = setInterval(() => {
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((cooldownEndTime - now) / 1000));
            setTimeRemaining(remaining);

            if (remaining === 0) {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [cooldownEndTime, showRefreshButton]);

    const isCooldownActive = timeRemaining > 0;

    const handleRefreshClick = async () => {
        if (isCooldownActive) {
            setShowCooldownWarning(true);
            // Hide warning after specified duration
            setTimeout(() => setShowCooldownWarning(false), cooldownWarningDuration * 1000);
            return;
        }

        if (onRefresh) {
            await onRefresh();
            // Start new cooldown after successful refresh
            const newCooldown = Date.now() + (cooldownDuration * 1000);
            setCooldownEndTime(newCooldown);
            setTimeRemaining(cooldownDuration);
        }
    };

    // Calculate if we need action buttons
    const hasActions = showRefreshButton || showSuccessButton || showRequiredIcon || showActionButton || forceSpacing;
    // Use size as default for both inputHeight and buttonWidth, allow overrides
    // Buttons need more height (56px = 40px min-height + 16px padding) to prevent clipping
    const defaultHeight = isButton ? '56px' : size;
    const actualInputHeight = inputHeight || defaultHeight;
    // Make button width responsive - smaller on mobile to allow more space for input
    const actualButtonWidth = hasActions 
        ? (buttonWidth || { xs: '32px', sm: size })
        : '0px';
    const actualGap = hasActions ? gap : '0px';
    
    // For text inputs, don't constrain height and allow overflow to show label
    const isTextInput = inputType === 'text';
    const inputContainerHeight = isTextInput || isButton ? 'auto' : actualInputHeight;
    const inputContainerOverflow = isTextInput || isButton ? 'visible' : 'hidden';
    // When text inputs have action buttons, use center alignment so Typography can align with icons
    // Otherwise use flex-start to allow labels to show above inputs
    const inputContainerAlignItems = (isTextInput && hasActions) ? 'center' : (isTextInput || isButton ? 'flex-start' : 'center');
    // Only apply margin-top for text inputs without action buttons (to allow labels to show)
    // When action buttons are present, we want center alignment, so no margin
    const buttonMarginTop = (isTextInput && !hasActions) ? '8px' : '0px';
    // Use flex-start for buttons to prevent extra space above them
    const parentAlignItems = (isTextInput && hasActions) ? 'center' : (isTextInput || isButton ? 'flex-start' : 'center');

    // Use center alignment for Typography elements to align with icons, flex-start for other text inputs
    return (
        <div style={{ 
            display: 'flex', 
            alignItems: parentAlignItems, 
            width: '100%', 
            gap: actualGap,
            minWidth: 0, // Allow container to shrink
        }}>
            {/* Left Action Button Container */}
            {hasActions && (
                <Box sx={{
                    width: typeof actualButtonWidth === 'object' ? actualButtonWidth : actualButtonWidth,
                    minWidth: typeof actualButtonWidth === 'object' ? actualButtonWidth : actualButtonWidth,
                    maxWidth: typeof actualButtonWidth === 'object' ? actualButtonWidth : actualButtonWidth,
                    height: actualInputHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    marginTop: buttonMarginTop,
                    flexShrink: 0, // Prevent left action from shrinking
                    flexGrow: 0,
                }}>
                    {/* Refresh Button */}
                    {showRefreshButton && (
                        <Tooltip title={
                            isRefreshing
                                ? "Refreshing..."
                                : isCooldownActive
                                    ? `Please wait ${timeRemaining}s before refreshing`
                                    : refreshTooltip
                        }>
                            <span>
                                <IconButton
                                    onClick={handleRefreshClick}
                                    disabled={isRefreshing || isCooldownActive}
                                    sx={{
                                        color: isCooldownActive ? theme.colors.overlayTextDisabled : 'inherit',
                                        '&:hover': {
                                            backgroundColor: isCooldownActive ? 'transparent' : theme.colors.overlayHover,
                                        },
                                        '&:disabled': {
                                            color: theme.colors.overlayTextDisabled,
                                        }
                                    }}
                                >
                                    {React.createElement(refreshIcon, {
                                        size: 24,
                                        weight: "bold",
                                        color: theme.colors.brandWarning,
                                        style: {
                                            animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                                        }
                                    })}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    {/* Success Button */}
                    {showSuccessButton && (
                        <Tooltip title={successTooltip} arrow>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: actualButtonWidth,
                                    height: actualInputHeight
                                }}
                            >
                                {React.createElement(successIcon, {
                                    size: 24,
                                    color: successColor || theme.colors.brandSuccess,
                                    weight: "bold"
                                })}
                            </Box>
                        </Tooltip>
                    )}

                    {/* Required Field Indicator */}
                    {showRequiredIcon && (
                        <Tooltip title={requiredTooltip} arrow>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: actualButtonWidth,
                                    height: actualInputHeight
                                }}
                            >
                                {React.createElement(requiredIcon, {
                                    size: requiredIconSize,
                                    color: requiredIconColor,
                                    weight: "bold"
                                })}
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            )}

            {/* Input Container */}
            <Box
                sx={{
                    flex: 1,
                    position: 'relative',
                    minWidth: 0, // Allow input to shrink below content size
                    overflow: inputContainerOverflow,
                    display: 'flex',
                    alignItems: inputContainerAlignItems,
                    height: inputContainerHeight,
                }}
            >
                {children}
            </Box>

            {/* Right Action Button Container */}
            {hasActions && (
                <Box sx={{
                    width: typeof actualButtonWidth === 'object' ? actualButtonWidth : actualButtonWidth,
                    minWidth: typeof actualButtonWidth === 'object' ? actualButtonWidth : actualButtonWidth,
                    maxWidth: typeof actualButtonWidth === 'object' ? actualButtonWidth : actualButtonWidth,
                    height: actualInputHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    flexShrink: 0,
                    marginTop: buttonMarginTop,
                }}>
                    {/* Right-side Action Button */}
                    {showActionButton && actionIcon && (
                        <Tooltip title={actionTooltip} arrow>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {actionIconType === 'mui' ? (
                                    <Box
                                        onClick={onActionClick}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: actionButtonDisabled ? 'not-allowed' : 'pointer',
                                            opacity: actionButtonDisabled ? 0.5 : 0.7,
                                            '&:hover': {
                                                opacity: actionButtonDisabled ? 0.5 : 1,
                                                backgroundColor: actionButtonDisabled ? 'transparent' : theme.colors.overlayHover,
                                            },
                                            ...actionIconSx
                                        }}
                                    >
                                        {React.createElement(actionIcon, {
                                            sx: {
                                                fontSize: `${actionIconSize}px`,
                                                color: actionIconColor || theme.colors.text,
                                                ...(actionIconSx || {})
                                            }
                                        })}
                                    </Box>
                                ) : (
                                    <IconButton
                                        onClick={onActionClick}
                                        disabled={actionButtonDisabled}
                                        size="small"
                                        sx={{
                                            color: 'inherit',
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover,
                                            },
                                            '&:disabled': {
                                                color: theme.colors.overlayDisabled,
                                            }
                                        }}
                                    >
                                        {React.createElement(actionIcon, {
                                            size: actionIconSize,
                                            color: actionIconColor,
                                            weight: "bold"
                                        })}
                                    </IconButton>
                                )}
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}

            {/* Cooldown Warning */}
            {showCooldownWarning && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    right: '0',
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: theme.colors.brandWarningLight,
                    color: theme.colors.text,
                    borderRadius: '4px',
                    fontSize: '12px',
                    textAlign: 'center',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                    Please wait {timeRemaining} seconds before refreshing.
                </div>
            )}

            {/* CSS for spin animation */}
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
};

export default WithActions;
